import { LinearProgress, MenuItem, Select } from '@mui/material';
import { useCallback, useState } from 'react';
import { useUpdateWorkpackStatusMutation, WorkpackStatus } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { primary } from '@/theme/colors';
import { useWorkpackStatusLabels } from '@/hooks/useWorkpackStatusLabels';

type Props = {
  workpackId: string;
  status: WorkpackStatus | null | undefined;
  showAlternateStatuses: boolean;
  onStatusChange?: () => void; // Callback to refresh parent data
};

const ALTERNATE_STATUSES = new Set([
  WorkpackStatus.DesktopReviewInProgress,
  WorkpackStatus.DesktopReviewComplete,
  WorkpackStatus.FieldVerificationInProgress,
  WorkpackStatus.FieldVerificationComplete,
  WorkpackStatus.FinalReviewInProgress,
  WorkpackStatus.FinalReviewComplete,
]);

export const StatusChangeDropDown = ({
  workpackId,
  status,
  showAlternateStatuses = false,
  onStatusChange,
}: Props) => {
  const [selectedStatus, setSelectedStatus] = useState<WorkpackStatus | ''>(status || '');
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const { statusOptions, getWorkpackStatusLabel } = useWorkpackStatusLabels();

  const [updateWorkpackStatus, { loading }] = useUpdateWorkpackStatusMutation();

  const handleStatusChange = useCallback(
    (updatedStatus: WorkpackStatus) => {
      updateWorkpackStatus({
        variables: { workpackId, status: updatedStatus },
        onCompleted: () => {
          showSnackBar('Workpack status has been updated successfully', 'success');
          setSelectedStatus(updatedStatus);
          // Trigger parent refresh to update POI table
          onStatusChange?.();
        },
        onError: (error) => {
          showSnackBar(error.message, 'error');
        },
      });
    },
    [showSnackBar, updateWorkpackStatus, workpackId, onStatusChange]
  );

  const renderValue = (value: WorkpackStatus | '') => {
    return value ? (
      getWorkpackStatusLabel(value)
    ) : (
      <span style={{ color: primary.LightGrey }}>Update Status</span>
    );
  };

  return (
    <>
      {loading ? (
        <LinearProgress color="info" sx={{ height: 7 }} />
      ) : (
        <Select
          value={selectedStatus}
          onChange={({ target: { value } }) => handleStatusChange(value as WorkpackStatus)}
          sx={{ height: 32, width: 'fit-content', fontSize: '1.2rem' }}
          fullWidth
          displayEmpty
          renderValue={renderValue}
        >
          {statusOptions
            .filter(({ value }) =>
              showAlternateStatuses
                ? ALTERNATE_STATUSES.has(value as WorkpackStatus)
                : !ALTERNATE_STATUSES.has(value as WorkpackStatus)
            )
            .map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
        </Select>
      )}
    </>
  );
};
